import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { useToast } from '../../context/ToastContext';

const WhatsAppSettings = () => {
  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configStatus, setConfigStatus] = useState({ configured: false, provider: '' });

  // Settings state
  const [settings, setSettings] = useState({
    IsEnabled: true,
    SendMode: 'api', // 'api' or 'web'
    AutoSendSale: false,
    AutoSendPurchase: false,
    SendToCustomer: true,
    SendToOwner: false,
    OwnerMobile: '',
    CustomMessage: 'Thank you for your business!',
    ShowPrintPopupSale: true,
    ShowPrintPopupPurchase: true,
    firmDefaultContact: '',
    firmName: ''
  });

  // Test message state
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testFeedback, setTestFeedback] = useState(null);

  // Fetch current WhatsApp settings and gateway status
  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [statusRes, setRes] = await Promise.all([
        axiosClient.get('/whatsapp/config-status').catch(() => ({ success: false })),
        axiosClient.get('/whatsapp/settings')
      ]);

      if (statusRes.success) {
        setConfigStatus(statusRes);
      }

      if (setRes.success && setRes.settings) {
        const s = setRes.settings;
        setSettings({
          IsEnabled: Boolean(s.IsEnabled === 1 || s.IsEnabled === true),
          SendMode: s.SendMode === 'web' ? 'web' : 'api',
          AutoSendSale: Boolean(s.AutoSendSale === 1 || s.AutoSendSale === true),
          AutoSendPurchase: Boolean(s.AutoSendPurchase === 1 || s.AutoSendPurchase === true),
          SendToCustomer: Boolean(s.SendToCustomer === 1 || s.SendToCustomer === true),
          SendToOwner: Boolean(s.SendToOwner === 1 || s.SendToOwner === true),
          OwnerMobile: s.OwnerMobile || s.firmDefaultContact || '',
          CustomMessage: s.CustomMessage || 'Thank you for your business!',
          ShowPrintPopupSale: s.ShowPrintPopupSale !== 0 && s.ShowPrintPopupSale !== false,
          ShowPrintPopupPurchase: s.ShowPrintPopupPurchase !== 0 && s.ShowPrintPopupPurchase !== false,
          firmDefaultContact: s.firmDefaultContact || '',
          firmName: s.firmName || ''
        });

        if (!testPhone && (s.OwnerMobile || s.firmDefaultContact)) {
          setTestPhone(s.OwnerMobile || s.firmDefaultContact);
        }
      }
    } catch (err) {
      console.error('Error loading WhatsApp settings:', err);
      showError(err.message || 'Failed to load WhatsApp settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save Settings
  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      const res = await axiosClient.put('/whatsapp/settings', {
        IsEnabled: settings.IsEnabled ? 1 : 0,
        SendMode: settings.SendMode === 'web' ? 'web' : 'api',
        AutoSendSale: settings.AutoSendSale ? 1 : 0,
        AutoSendPurchase: settings.AutoSendPurchase ? 1 : 0,
        SendToCustomer: settings.SendToCustomer ? 1 : 0,
        SendToOwner: settings.SendToOwner ? 1 : 0,
        OwnerMobile: settings.OwnerMobile.trim(),
        CustomMessage: settings.CustomMessage.trim(),
        ShowPrintPopupSale: settings.ShowPrintPopupSale ? 1 : 0,
        ShowPrintPopupPurchase: settings.ShowPrintPopupPurchase ? 1 : 0
      });

      if (res.success) {
        showSuccess('WhatsApp settings updated successfully!');
      } else {
        throw new Error(res.message || 'Failed to update settings');
      }
    } catch (err) {
      console.error('Save Settings Error:', err);
      showError(err.message || 'Error saving WhatsApp settings.');
    } finally {
      setSaving(false);
    }
  };

  // Send Test WhatsApp Message
  const handleSendTest = async (e) => {
    e.preventDefault();
    const cleanNumber = testPhone.replace(/\D/g, '');
    if (!cleanNumber || cleanNumber.length < 10) {
      showError('Please enter a valid 10-digit mobile number for the test message.');
      return;
    }

    setSendingTest(true);
    setTestFeedback(null);

    try {
      const res = await axiosClient.post('/whatsapp/test', {
        phone: cleanNumber,
        testMessage: `🔔 *WhatsApp Gateway Verification*\n\nYour WhatsApp sender is active and properly connected for *${settings.firmName || 'Fish Shop'}*.\n\nTime: ${new Date().toLocaleString('en-IN')}`
      });

      if (res.success) {
        setTestFeedback({
          type: 'success',
          msg: `✅ Test message successfully sent to ${cleanNumber}!`
        });
        showSuccess(`Test WhatsApp sent to ${cleanNumber}!`);
      } else {
        throw new Error(res.message || 'Gateway returned an error.');
      }
    } catch (err) {
      const msg = err.message || 'Failed to send test message.';
      setTestFeedback({
        type: 'danger',
        msg: `❌ Error: ${msg}`
      });
      showError(msg);
    } finally {
      setSendingTest(false);
    }
  };

  const copyFirmContactToOwner = () => {
    if (settings.firmDefaultContact) {
      setSettings((prev) => ({ ...prev, OwnerMobile: settings.firmDefaultContact }));
      showSuccess(`Owner mobile set to firm contact: ${settings.firmDefaultContact}`);
    } else {
      showError('No firm contact number found in Firm Profile.');
    }
  };

  if (loading) {
    return (
      <div className="container-fluid p-4 text-center">
        <div className="spinner-border text-success" role="status"></div>
        <div className="mt-2 text-muted fw-semibold">Loading WhatsApp settings...</div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-3 p-md-4 max-w-1200">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 pb-3 border-bottom">
        <div>
          <div className="d-flex align-items-center gap-2">
            <div className="bg-success text-white p-2 rounded-3 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '42px', height: '42px' }}>
              <i className="bi bi-whatsapp fs-4"></i>
            </div>
            <div>
              <h3 className="fw-bold mb-0 text-dark">WhatsApp Settings</h3>
              <p className="text-muted small mb-0">
                Configure WhatsApp sender status, automated bill dispatch, and recipient routing
              </p>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span className={`badge ${settings.IsEnabled ? 'bg-success' : 'bg-danger'} px-3 py-2 fs-6 shadow-sm`}>
            <i className={`bi ${settings.IsEnabled ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>
            WhatsApp Sender: {settings.IsEnabled ? 'ENABLED' : 'DISABLED'}
          </span>
          <button
            type="button"
            className="btn btn-primary px-4 fw-semibold shadow-sm d-flex align-items-center gap-2"
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-floppy-fill"></i> Save Settings
              </>
            )}
          </button>
        </div>
      </div>

      <div className="row g-4">
        {/* Left Column: Settings Configuration */}
        <div className="col-12 col-lg-8">
          <form onSubmit={handleSaveSettings}>
            
            {/* 1. Master WhatsApp Sender Switch Card (Vyapar Style) */}
            <div className={`card shadow-sm border-0 mb-4 ${settings.IsEnabled ? 'border-start border-4 border-success' : 'border-start border-4 border-danger'}`}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span className="fs-5 fw-bold text-dark">WhatsApp Sender Service</span>
                      <span className={`badge ${settings.IsEnabled ? 'bg-success bg-opacity-10 text-success border border-success' : 'bg-danger bg-opacity-10 text-danger border border-danger'}`}>
                        {settings.IsEnabled ? 'Active' : 'Off'}
                      </span>
                    </div>
                    <p className="text-muted small mb-0" style={{ maxWidth: '480px' }}>
                      Master switch for WhatsApp integration. When turned <strong>OFF</strong>, all automatic sending and manual API dispatches are disabled.
                    </p>
                  </div>

                  {/* Big Toggle Switch */}
                  <div className="form-check form-switch form-switch-lg fs-4 mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="masterWhatsAppSwitch"
                      style={{ cursor: 'pointer', width: '3em', height: '1.6em' }}
                      checked={settings.IsEnabled}
                      onChange={(e) => setSettings({ ...settings, IsEnabled: e.target.checked })}
                    />
                  </div>
                </div>

                {!settings.IsEnabled && (
                  <div className="alert alert-warning d-flex align-items-center gap-2 mt-3 mb-0 py-2">
                    <i className="bi bi-exclamation-triangle-fill fs-5 text-warning flex-shrink-0"></i>
                    <div className="small">
                      <strong>WhatsApp Sender is currently turned OFF.</strong> Invoices will not be sent automatically or from print dialogs until you re-enable this switch.
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 2. WhatsApp Sending Method: Using WhatsApp API or WhatsApp Web */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                <i className="bi bi-sliders text-primary"></i>
                <h5 className="mb-0 fw-bold fs-6">WhatsApp Sending Method</h5>
              </div>
              <div className="card-body p-4">
                <div className="row g-3">
                  {/* Option A: Using WhatsApp Business API */}
                  <div className="col-12 col-md-6">
                    <div
                      className={`p-3 border rounded-3 h-100 ${settings.SendMode === 'api' ? 'border-primary bg-primary bg-opacity-10 shadow-sm' : 'bg-white'}`}
                      onClick={() => setSettings({ ...settings, SendMode: 'api' })}
                      style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="whatsappSendMode"
                          id="modeApi"
                          checked={settings.SendMode === 'api'}
                          onChange={() => setSettings({ ...settings, SendMode: 'api' })}
                          style={{ cursor: 'pointer' }}
                        />
                        <label className="form-check-label fw-bold text-dark fs-6 ms-1" htmlFor="modeApi" style={{ cursor: 'pointer' }}>
                          <i className="bi bi-cloud-arrow-up-fill text-primary me-1"></i> Using WhatsApp API
                        </label>
                      </div>
                      <p className="text-muted small mb-0 ps-4">
                        Automatic background cloud sending directly to party and firm owner mobile numbers without opening WhatsApp Web or application.
                      </p>
                    </div>
                  </div>

                  {/* Option B: Using WhatsApp Web / Mobile Application */}
                  <div className="col-12 col-md-6">
                    <div
                      className={`p-3 border rounded-3 h-100 ${settings.SendMode === 'web' ? 'border-success bg-success bg-opacity-10 shadow-sm' : 'bg-white'}`}
                      onClick={() => setSettings({ ...settings, SendMode: 'web' })}
                      style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="whatsappSendMode"
                          id="modeWeb"
                          checked={settings.SendMode === 'web'}
                          onChange={() => setSettings({ ...settings, SendMode: 'web' })}
                          style={{ cursor: 'pointer' }}
                        />
                        <label className="form-check-label fw-bold text-dark fs-6 ms-1" htmlFor="modeWeb" style={{ cursor: 'pointer' }}>
                          <i className="bi bi-whatsapp text-success me-1"></i> Using WhatsApp Web / App
                        </label>
                      </div>
                      <p className="text-muted small mb-0 ps-4">
                        Directly opens WhatsApp Web in browser on PC or WhatsApp application on mobile with pre-filled bill message and direct bill link.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Recipient Routing (Customer & Firm Owner) */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                <i className="bi bi-people-fill text-primary"></i>
                <h5 className="mb-0 fw-bold fs-6">Direct Recipient Routing</h5>
              </div>
              <div className="card-body p-4">
                <div className="row g-3">
                  {/* Recipient 1: Customer */}
                  <div className="col-12">
                    <div className="p-3 border rounded-3 bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div>
                        <div className="fw-bold text-dark d-flex align-items-center gap-2">
                          <i className="bi bi-person-check-fill text-success fs-5"></i>
                          Send to Customer / Party
                        </div>
                        <p className="text-muted small mb-0">
                          Sends invoice PDF and details directly to the customer or vendor contact mobile number.
                        </p>
                      </div>
                      <div className="form-check form-switch fs-5 mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="sendToCustomerSwitch"
                          style={{ cursor: 'pointer' }}
                          checked={settings.SendToCustomer}
                          onChange={(e) => setSettings({ ...settings, SendToCustomer: e.target.checked })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Recipient 2: Firm Owner */}
                  <div className="col-12">
                    <div className="p-3 border rounded-3 bg-light">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <div>
                          <div className="fw-bold text-dark d-flex align-items-center gap-2">
                            <i className="bi bi-building-check text-primary fs-5"></i>
                            Send Copy to Firm Owner Contact No.
                          </div>
                          <p className="text-muted small mb-0">
                            Sends an instant copy of every sale and purchase entry to the firm owner's WhatsApp number.
                          </p>
                        </div>
                        <div className="form-check form-switch fs-5 mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="sendToOwnerSwitch"
                            style={{ cursor: 'pointer' }}
                            checked={settings.SendToOwner}
                            onChange={(e) => setSettings({ ...settings, SendToOwner: e.target.checked })}
                          />
                        </div>
                      </div>

                      {/* Firm Owner Number Input */}
                      <div className="ps-md-4 pt-2 border-top">
                        <label className="form-label small fw-bold text-dark mb-1 d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <span>
                            <i className="bi bi-telephone-plus text-primary me-1"></i> Firm Owner WhatsApp Mobile Number(s):
                          </span>
                          <span className="badge bg-info bg-opacity-10 text-primary border border-info small">
                            Separate 2 numbers with comma (,)
                          </span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white text-muted">
                            <i className="bi bi-whatsapp text-success"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. 9224700288, 9881914831"
                            value={settings.OwnerMobile}
                            onChange={(e) => setSettings({ ...settings, OwnerMobile: e.target.value })}
                          />
                          {settings.firmDefaultContact && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm px-3"
                              onClick={copyFirmContactToOwner}
                              title="Copy contact number saved in Firm Profile"
                            >
                              <i className="bi bi-arrow-repeat me-1"></i> Use Firm Contact ({settings.firmDefaultContact})
                            </button>
                          )}
                        </div>
                        <div className="form-text small text-muted">
                          💡 You can enter two numbers separated by a comma (e.g. <strong>9224700288, 9881914831</strong>). Every owner number will receive duplicate copies of bills.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Automatic Sending on Save (Sale & Purchase) */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                <i className="bi bi-lightning-charge-fill text-warning"></i>
                <h5 className="mb-0 fw-bold fs-6">Entry Save Automation (Vyapar Style)</h5>
              </div>
              <div className="card-body p-4">
                <div className="row g-3">
                  {/* Auto-send Sale */}
                  <div className="col-12 col-md-6">
                    <div className="p-3 border rounded-3 h-100 bg-white">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="fw-bold text-dark">
                          <i className="bi bi-cart-check-fill text-success me-1"></i> Auto-Send on Sale Save
                        </div>
                        <div className="form-check form-switch fs-5 mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="autoSendSaleSwitch"
                            style={{ cursor: 'pointer' }}
                            checked={settings.AutoSendSale}
                            onChange={(e) => setSettings({ ...settings, AutoSendSale: e.target.checked })}
                          />
                        </div>
                      </div>
                      <p className="text-muted small mb-0">
                        When you click "Save Sale Bill", automatically dispatch the WhatsApp invoice immediately to Customer and/or Owner.
                      </p>
                    </div>
                  </div>

                  {/* Auto-send Purchase */}
                  <div className="col-12 col-md-6">
                    <div className="p-3 border rounded-3 h-100 bg-white">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="fw-bold text-dark">
                          <i className="bi bi-bag-check-fill text-primary me-1"></i> Auto-Send on Purchase Save
                        </div>
                        <div className="form-check form-switch fs-5 mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="autoSendPurchaseSwitch"
                            style={{ cursor: 'pointer' }}
                            checked={settings.AutoSendPurchase}
                            onChange={(e) => setSettings({ ...settings, AutoSendPurchase: e.target.checked })}
                          />
                        </div>
                      </div>
                      <p className="text-muted small mb-0">
                        When you click "Save Purchase", automatically dispatch the WhatsApp voucher immediately to Supplier and/or Owner.
                      </p>
                    </div>
                  </div>

                  {/* Print Popup on Sale Save */}
                  <div className="col-12 col-md-6">
                    <div className="p-3 border rounded-3 h-100 bg-white">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="fw-bold text-dark">
                          <i className="bi bi-printer-fill text-success me-1"></i> Show Print Popup on Sale Save
                        </div>
                        <div className="form-check form-switch fs-5 mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="showPrintPopupSaleSwitch"
                            style={{ cursor: 'pointer' }}
                            checked={settings.ShowPrintPopupSale}
                            onChange={(e) => setSettings({ ...settings, ShowPrintPopupSale: e.target.checked })}
                          />
                        </div>
                      </div>
                      <p className="text-muted small mb-0">
                        Show the Invoice Print & Share preview popup modal automatically right after saving a Sale bill. Turn off for silent background save.
                      </p>
                    </div>
                  </div>

                  {/* Print Popup on Purchase Save */}
                  <div className="col-12 col-md-6">
                    <div className="p-3 border rounded-3 h-100 bg-white">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="fw-bold text-dark">
                          <i className="bi bi-printer-fill text-primary me-1"></i> Show Print Popup on Purchase Save
                        </div>
                        <div className="form-check form-switch fs-5 mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="showPrintPopupPurchaseSwitch"
                            style={{ cursor: 'pointer' }}
                            checked={settings.ShowPrintPopupPurchase}
                            onChange={(e) => setSettings({ ...settings, ShowPrintPopupPurchase: e.target.checked })}
                          />
                        </div>
                      </div>
                      <p className="text-muted small mb-0">
                        Show the Voucher Print & Share preview popup modal automatically right after saving a Purchase entry. Turn off for silent background save.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Custom Message Footer Note */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                <i className="bi bi-chat-quote-fill text-info"></i>
                <h5 className="mb-0 fw-bold fs-6">Custom Note / Greeting</h5>
              </div>
              <div className="card-body p-4">
                <label className="form-label small fw-bold text-dark mb-1">
                  Message Footer Greeting:
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Thank you for your business!"
                  value={settings.CustomMessage}
                  onChange={(e) => setSettings({ ...settings, CustomMessage: e.target.value })}
                />
                <div className="form-text small text-muted">
                  Appears at the bottom of generated WhatsApp notifications and PDF document captions.
                </div>
              </div>
            </div>

            {/* Bottom Save Button */}
            <div className="d-flex justify-content-end gap-2 mb-4">
              <button
                type="submit"
                className="btn btn-primary px-5 py-2 fw-semibold shadow-sm"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Saving Changes...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check2-all me-1"></i> Save All WhatsApp Settings
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Right Column: Status & Test Gateway */}
        <div className="col-12 col-lg-4">
          {/* Gateway Status Card */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
              <i className="bi bi-hdd-network-fill text-secondary"></i>
              <h5 className="mb-0 fw-bold fs-6">WhatsApp Gateway Status</h5>
            </div>
            <div className="card-body p-4">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className={`badge ${configStatus.configured ? 'bg-success' : 'bg-secondary'} px-2 py-1`}>
                  {configStatus.configured ? 'Configured in .env' : 'Not Configured'}
                </span>
                <span className="badge bg-light text-dark border">
                  Provider: {configStatus.provider || 'synicate'}
                </span>
              </div>

              <div className="list-group list-group-flush small">
                <div className="list-group-item px-0 d-flex justify-content-between align-items-center">
                  <span className="text-muted">Master Sender:</span>
                  <span className={`fw-bold ${settings.IsEnabled ? 'text-success' : 'text-danger'}`}>
                    {settings.IsEnabled ? '● Active' : '○ Off'}
                  </span>
                </div>
                <div className="list-group-item px-0 d-flex justify-content-between align-items-center">
                  <span className="text-muted">Sending Method:</span>
                  <span className={`fw-bold ${settings.SendMode === 'web' ? 'text-success' : 'text-primary'}`}>
                    {settings.SendMode === 'web' ? 'WhatsApp Web / App' : 'WhatsApp API'}
                  </span>
                </div>
                <div className="list-group-item px-0 d-flex justify-content-between align-items-center">
                  <span className="text-muted">Send to Customer:</span>
                  <span className={`fw-bold ${settings.SendToCustomer ? 'text-success' : 'text-muted'}`}>
                    {settings.SendToCustomer ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="list-group-item px-0 d-flex justify-content-between align-items-center">
                  <span className="text-muted">Send to Firm Owner:</span>
                  <span className={`fw-bold ${settings.SendToOwner ? 'text-primary' : 'text-muted'}`}>
                    {settings.SendToOwner ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="list-group-item px-0 d-flex justify-content-between align-items-center">
                  <span className="text-muted">Auto-Send Sale:</span>
                  <span className={`fw-bold ${settings.AutoSendSale ? 'text-success' : 'text-muted'}`}>
                    {settings.AutoSendSale ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="list-group-item px-0 d-flex justify-content-between align-items-center">
                  <span className="text-muted">Auto-Send Purchase:</span>
                  <span className={`fw-bold ${settings.AutoSendPurchase ? 'text-primary' : 'text-muted'}`}>
                    {settings.AutoSendPurchase ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Test WhatsApp Sender Tool */}
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
              <i className="bi bi-send-check-fill text-success"></i>
              <h5 className="mb-0 fw-bold fs-6">Test WhatsApp Sender</h5>
            </div>
            <div className="card-body p-4">
              <p className="text-muted small">
                Verify that your WhatsApp API credentials and gateway delivery work by sending a test message to any number.
              </p>

              <form onSubmit={handleSendTest}>
                <div className="mb-3">
                  <label className="form-label small fw-bold text-dark">
                    Test Mobile Number:
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-white text-muted">+91</span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="10-digit mobile"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-success w-100 fw-semibold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                  disabled={sendingTest || !testPhone}
                >
                  {sendingTest ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                      Sending Test...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-whatsapp"></i> Send Test Message
                    </>
                  )}
                </button>
              </form>

              {testFeedback && (
                <div className={`alert alert-${testFeedback.type} small mt-3 mb-0 py-2`}>
                  {testFeedback.msg}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSettings;
